import React, { useEffect, useRef } from "react";
import { Navigate, useParams, useSearchParams } from "react-router-dom";

import { usePartyGameByShellId } from "../../hooks/usePartyCatalog";
import { partyShellSupportsTrial } from "../../party/partyTrialMockData";
import PartyPlayActing from "./PartyPlayActing";
import PartyPlayAnswerBluff from "./PartyPlayAnswerBluff";
import PartyPlayCategoryGame from "./PartyPlayCategoryGame";
import PartyPlayDareGame from "./PartyPlayDareGame";
import PartyPlayGuessIt from "./PartyPlayGuessIt";
import PartyPlayImposter from "./PartyPlayImposter";
import PartyPlayImposterOnlineOnly from "./PartyPlayImposterOnlineOnly";
import PartyPlayPlaceholder from "./PartyPlayPlaceholder";
import PartyPlayQuickQuestions from "./PartyPlayQuickQuestions";
import PartyPlayWhoAmI from "./PartyPlayWhoAmI";
import { MixpanelEvents, trackEvent } from "../../utils/mixpanel";

export default function PartyPlayPage() {
  const { shellId } = useParams<{ shellId: string }>();
  const [searchParams] = useSearchParams();
  const trialParam = searchParams.get("trial") === "1";
  const detailQ = usePartyGameByShellId(shellId);
  const trialSessionTrackedRef = useRef<string | null>(null);

  const trialNotAllowed =
    Boolean(shellId) &&
    trialParam &&
    (shellId === "bluff" ||
      !partyShellSupportsTrial(shellId) ||
      (!detailQ.isLoading && detailQ.data != null && !detailQ.data.consumesCredit));

  const trialSessionActive =
    Boolean(shellId && trialParam) &&
    shellId !== "bluff" &&
    partyShellSupportsTrial(shellId) &&
    !detailQ.isLoading &&
    Boolean(detailQ.data?.consumesCredit);

  useEffect(() => {
    if (!shellId) return;
    trackEvent(MixpanelEvents.PARTY_PLAY_SHELL_VIEWED, {
      shell_id: shellId,
      trial: trialParam,
      consumes_credit: detailQ.data?.consumesCredit ?? null,
      trial_blocked: trialParam ? trialNotAllowed : null,
    });
  }, [shellId, trialParam, detailQ.data?.consumesCredit, trialNotAllowed]);

  useEffect(() => {
    if (!shellId || !trialSessionActive) return;
    const dedupeKey = `${shellId}|trial`;
    if (trialSessionTrackedRef.current === dedupeKey) return;
    trialSessionTrackedRef.current = dedupeKey;
    trackEvent(MixpanelEvents.PARTY_TRIAL_SESSION_STARTED, { shell_id: shellId });
  }, [shellId, trialSessionActive]);

  if (!shellId) return null;

  if (trialNotAllowed) {
    return <Navigate to={`/party/play/${shellId}`} replace />;
  }

  switch (shellId) {
    case "imposter":
      return <PartyPlayImposter />;
    case "imposter-online":
      return <PartyPlayImposterOnlineOnly />;
    case "guess":
      return <PartyPlayGuessIt />;
    case "act":
      return <PartyPlayActing />;
    case "who":
      return <PartyPlayWhoAmI />;
    case "fast":
      return <PartyPlayQuickQuestions />;
    case "category":
      return <PartyPlayCategoryGame />;
    case "challenge":
      return <PartyPlayDareGame />;
    case "bluff":
      return <PartyPlayAnswerBluff />;
    default:
      return <PartyPlayPlaceholder shellId={shellId} />;
  }
}
